import type { Column, HeaderGroup } from '@tanstack/react-table';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TABLE_STICKY_HEADER_Z_INDEX } from '@/shared/lib/constants';
import { NestedStickyContext } from '../helpers/nested-sticky-context';
import type { PinnedColumnInfo, StickyConfig } from '../helpers/types';
import { useProxyScroll } from '../hooks/useProxyScroll';
import { CNTable } from './CNTable';
import { ScrollProxy } from './ScrollProxy';
import { TableBody } from './TableBody';
import { TableColgroup } from './TableColgroup';
import { TableHeader } from './TableHeader';
import { TableHeaderRows } from './TableHeaderRows';

type StickyTableWrapperProps<TData> = {
  /** 子要素（ボディのコンテンツ） */
  children: ReactNode;
  /** ヘッダーグループ */
  headerGroups: HeaderGroup<TData>[];
  /** リーフカラム */
  leafColumns: Column<TData, unknown>[];
  /** カラムスタイル */
  columnStyles: Record<string, CSSProperties | undefined>;
  /** ウェイト指定スタイル */
  weightedLeafWidthStyles: Record<string, CSSProperties>;
  /** ピン留めカラムスタイル */
  pinnedColumnStyles: Record<string, PinnedColumnInfo>;
  /** テーブル固定幅 */
  tableFixedWidth: number;
  /** Sticky設定 */
  stickyConfig: StickyConfig;
  /** stickyヘッダーのz-index（ネストされたテーブルでは親より低い値を使用） */
  zIndex?: number;
  /** ボディテーブルへの参照（scrollToRowId機能用） */
  bodyTableRef?: RefObject<HTMLTableElement | null>;
};

/**
 * Stickyヘッダー対応のテーブルラッパーコンポーネント
 *
 * ## なぜこの構造が必要か
 *
 * 通常のテーブルでは `position: sticky` と `overflow-x: auto` を両立できない。
 * `overflow: auto/hidden/scroll` を持つ要素は新しいスクロールコンテキストを作るため、
 * その中の sticky 要素はその範囲内でしか動作しない。
 *
 * ## 解決策: プロキシスクロール方式
 *
 * ```
 * <div ref={containerRef}>              ← ピン留めセルDOM検索のスコープ
 *   <div class="sticky">                ← ページ全体のスクロールに対してsticky
 *     <table>ヘッダー</table>            ← transform: translateX で横移動
 *   </div>
 *   <div style="overflow-x: clip">      ← overflow-x: clip（スクロールコンテキストを作らず、はみ出しを隠す）
 *     <table>ボディ</table>              ← transform: translateX で横移動
 *   </div>
 *   <ScrollProxy />                      ← 実際の横スクロールバーを提供
 * </div>
 * ```
 *
 * - ヘッダー/ボディ: `overflow-x` を使わず `transform: translateX` で横移動
 * - ScrollProxy: 実際のスクロールバーを持ち、スクロール量を `useProxyScroll` で同期
 * - ネストされたテーブルの `position: sticky` が正しく動作する
 *
 * @see ScrollProxy
 * @see useProxyScroll
 */
export const StickyTableWrapper = <TData,>({
  children,
  headerGroups,
  leafColumns,
  columnStyles,
  weightedLeafWidthStyles,
  pinnedColumnStyles,
  tableFixedWidth,
  stickyConfig,
  zIndex,
  bodyTableRef,
}: StickyTableWrapperProps<TData>) => {
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const proxyRef = useRef<HTMLDivElement>(null);
  const headerTableRef = useRef<HTMLTableElement>(null);
  const internalBodyTableRef = useRef<HTMLTableElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 外部のbodyTableRefと内部のinternalBodyTableRefの両方を同じDOM要素に接続するコールバックRef
  const bodyTableCallbackRef = useCallback(
    (node: HTMLTableElement | null) => {
      internalBodyTableRef.current = node;
      if (bodyTableRef) {
        (bodyTableRef as React.MutableRefObject<HTMLTableElement | null>).current = node;
      }
    },
    [bodyTableRef],
  );

  useProxyScroll({ proxyRef, headerTableRef, bodyTableRef: internalBodyTableRef, containerRef });

  const { offsetHeader = 0, maxBodyHeight } = stickyConfig;
  const resolvedZIndex = zIndex ?? TABLE_STICKY_HEADER_Z_INDEX;

  // ヘッダー高さを測定してネストされたテーブルのsticky位置を算出
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setHeaderHeight(entry.borderBoxSize[0]?.blockSize ?? el.getBoundingClientRect().height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const nestedStickyValue = {
    offsetHeader: offsetHeader + headerHeight,
    zIndex: resolvedZIndex - 1,
    proxyRef,
    containerRef,
  };

  const tableStyle: CSSProperties = tableFixedWidth
    ? { minWidth: `${tableFixedWidth}px`, tableLayout: 'fixed' }
    : { tableLayout: 'fixed' };

  const bodyContainerStyle: CSSProperties | undefined = maxBodyHeight
    ? { maxHeight: `${maxBodyHeight}px`, overflowY: 'auto' }
    : undefined;

  return (
    <NestedStickyContext.Provider value={nestedStickyValue}>
      <div ref={containerRef}>
        {/* ヘッダーテーブル: overflow指定なし → stickyがページスクロールに追従 */}
        <div
          ref={headerContainerRef}
          className="sticky mb-0 overflow-hidden bg-background"
          style={{
            top: offsetHeader,
            zIndex: resolvedZIndex,
          }}
        >
          <CNTable ref={headerTableRef} style={tableStyle}>
            <TableColgroup
              columns={leafColumns}
              columnStyles={columnStyles}
              weightedLeafWidthStyles={weightedLeafWidthStyles}
            />
            <TableHeader>
              <TableHeaderRows
                headerGroups={headerGroups}
                columnStyles={columnStyles}
                weightedLeafWidthStyles={weightedLeafWidthStyles}
                pinnedColumnStyles={pinnedColumnStyles}
              />
            </TableHeader>
          </CNTable>
        </div>

        {/* ボディテーブル: overflow-x: clip（スクロールコンテキストを作らず、はみ出しを隠す → ネストテーブルのCSS stickyが有効） */}
        <div style={{ overflowX: 'clip', ...bodyContainerStyle }}>
          <CNTable ref={bodyTableCallbackRef} style={tableStyle}>
            <TableColgroup
              columns={leafColumns}
              columnStyles={columnStyles}
              weightedLeafWidthStyles={weightedLeafWidthStyles}
            />
            {/* 非表示のヘッダー: ボディテーブルの列幅をヘッダーテーブルと一致させるために必要 */}
            <TableHeader className="invisible h-0 overflow-hidden">
              <TableHeaderRows
                className="hidden h-0"
                headerGroups={headerGroups}
                columnStyles={columnStyles}
                weightedLeafWidthStyles={weightedLeafWidthStyles}
                pinnedColumnStyles={pinnedColumnStyles}
              />
            </TableHeader>
            <TableBody>{children}</TableBody>
          </CNTable>
        </div>

        {/* プロキシスクロールバー: 実際の横スクロールUIを提供 */}
        <ScrollProxy proxyRef={proxyRef} bodyTableRef={internalBodyTableRef} />
      </div>
    </NestedStickyContext.Provider>
  );
};
